<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const { t } = useI18n();

const options = computed(() => [
  { value: 'auto', label: t('COMMAND_BAR.COMMANDS.SYSTEM_MODE') },
  { value: 'light', label: t('COMMAND_BAR.COMMANDS.LIGHT_MODE') },
  { value: 'dark', label: t('COMMAND_BAR.COMMANDS.DARK_MODE') },
]);

const currentTheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);

const updateAppearance = value => {
  currentTheme.value = value;
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, value);
  const isOSOnDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;
  setColorTheme(isOSOnDarkMode);
};
</script>

<template>
  <div class="flex flex-col w-full gap-3">
    <div class="flex flex-col">
      <label class="text-sm font-medium text-n-slate-12">{{ label }}</label>
      <p v-if="description" class="mb-0 text-xs text-n-slate-11">
        {{ description }}
      </p>
    </div>
    <div class="grid grid-cols-3 gap-3 max-w-md">
      <button
        v-for="opt in options"
        :key="opt.value"
        type="button"
        class="flex flex-col items-start gap-2 group"
        @click="updateAppearance(opt.value)"
      >
        <div
          class="w-full aspect-[5/3] rounded-lg p-1 border-2 transition-colors overflow-hidden"
          :class="
            opt.value === currentTheme
              ? 'border-n-brand'
              : 'border-n-weak group-hover:border-n-strong'
          "
        >
          <!-- System / auto mockup: split light + dark -->
          <div
            v-if="opt.value === 'auto'"
            class="relative w-full h-full rounded-md overflow-hidden flex"
          >
            <div class="w-1/2 h-full bg-white border-r border-slate-200">
              <div class="absolute top-1 left-1 flex gap-0.5">
                <div class="size-1 rounded-full bg-slate-300" />
                <div class="size-1 rounded-full bg-slate-300" />
              </div>
              <div class="absolute left-1 top-4 space-y-1">
                <div class="w-3 h-0.5 rounded-sm bg-slate-200" />
                <div class="w-2 h-0.5 rounded-sm bg-slate-200" />
              </div>
            </div>
            <div class="w-1/2 h-full bg-slate-800 relative">
              <div
                class="absolute right-1 top-1 w-3 h-1 rounded-sm bg-blue-500"
              />
              <div class="absolute left-1 top-4 space-y-1">
                <div class="w-5 h-0.5 rounded-sm bg-slate-600" />
                <div class="w-6 h-0.5 rounded-sm bg-slate-600" />
                <div class="w-4 h-0.5 rounded-sm bg-slate-600" />
              </div>
            </div>
          </div>

          <!-- Light mockup -->
          <div
            v-else-if="opt.value === 'light'"
            class="relative w-full h-full rounded-md overflow-hidden bg-white"
          >
            <div class="absolute top-1 left-1 flex gap-0.5">
              <div class="size-1 rounded-full bg-slate-300" />
              <div class="size-1 rounded-full bg-slate-300" />
              <div class="size-1 rounded-full bg-slate-300" />
            </div>
            <div
              class="absolute right-1 top-1 w-3 h-1 rounded-sm bg-blue-500"
            />
            <div class="absolute left-0 top-4 bottom-0 w-1/4 bg-slate-50" />
            <div class="absolute left-[28%] right-1 top-4 space-y-1">
              <div class="w-3/4 h-0.5 rounded-sm bg-slate-200" />
              <div class="w-full h-0.5 rounded-sm bg-slate-200" />
              <div class="w-2/3 h-0.5 rounded-sm bg-slate-200" />
              <div class="w-1/2 h-0.5 rounded-sm bg-slate-200" />
            </div>
          </div>

          <!-- Dark mockup -->
          <div
            v-else
            class="relative w-full h-full rounded-md overflow-hidden bg-slate-900"
          >
            <div class="absolute top-1 left-1 flex gap-0.5">
              <div class="size-1 rounded-full bg-slate-700" />
              <div class="size-1 rounded-full bg-slate-700" />
              <div class="size-1 rounded-full bg-slate-700" />
            </div>
            <div
              class="absolute right-1 top-1 w-3 h-1 rounded-sm bg-blue-500"
            />
            <div class="absolute left-0 top-4 bottom-0 w-1/4 bg-slate-800" />
            <div class="absolute left-[28%] right-1 top-4 space-y-1">
              <div class="w-3/4 h-0.5 rounded-sm bg-slate-600" />
              <div class="w-full h-0.5 rounded-sm bg-slate-600" />
              <div class="w-2/3 h-0.5 rounded-sm bg-slate-600" />
              <div class="w-1/2 h-0.5 rounded-sm bg-slate-600" />
            </div>
          </div>
        </div>
        <span
          class="text-xs font-medium"
          :class="
            opt.value === currentTheme ? 'text-n-slate-12' : 'text-n-slate-11'
          "
        >
          {{ opt.label }}
        </span>
      </button>
    </div>
  </div>
</template>
